import React from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack, MdChevronRight } from 'react-icons/md';
import { DELTA_INTRO, DELTA_SECTIONS, OPERATING, PORTFOLIO } from '../../content/deck';
import { SlideHead, TONES, toneVars } from './DeckUI';
import SlideDeck from './SlideDeck';
import PolicyPackPage from './PolicyPackPage';
import ExecutionPage from './ExecutionPage';

// Project Delta — the reorganisation plan, in four parts.
//
// TOP DOWN, ONE DECISION PER SCREEN. Arriving here does not drop the reader into a
// document: the section says what it is, then offers its four parts with a sentence each,
// then a part opens in full. Each level answers "where am I and what is here" before asking
// "which one do you want", which is what a row of four bare tabs never did.
//
// The four parts are the deck's four slides, in the deck's order. The index lives in
// content/deck.js beside the slides themselves, route slugs included, so navigation and
// content cannot fall out of step.

const PAGES = {
  structure: <SlideDeck slide={PORTFOLIO} />,
  operating: <SlideDeck slide={OPERATING} />,
  policies: <PolicyPackPage />,
  execution: <ExecutionPage />,
};

export default function DeltaSection() {
  const { pathname } = useLocation();
  // Match on the path SEGMENT, not the tail, so a document inside a part
  // (/delta/policies/L0-CHARTER) still knows which part it belongs to.
  const segments = pathname.split('/').filter(Boolean).slice(1); // drop 'delta'
  const current = DELTA_SECTIONS.find((s) => s.slug === segments[0]);
  const deeper = current && segments.length > 1;

  return (
    <div>
      {/* The trail back, always exactly one level. From a part it goes to the section, from
          a document inside a part it goes to that part — a reader comparing two policies
          should not be thrown to the home screen between them. */}
      <div className="mb-4 flex items-center gap-2 text-[11px]">
        <Link to={deeper ? `/delta/${current.slug}` : current ? '/delta' : '/'}
              className="flex shrink-0 items-center gap-1 text-gray-400 transition-colors hover:text-brand-gold dark:text-gray-500">
          <MdArrowBack size={14} />
          {deeper ? current.title : current ? 'Project Delta' : 'All areas'}
        </Link>
        {current && !deeper && (
          <>
            <span className="text-gray-300 dark:text-gray-700">/</span>
            <span className="truncate font-medium text-gray-600 dark:text-gray-300">{current.title}</span>
          </>
        )}
      </div>

      <Routes>
        <Route index element={<DeltaIndex />} />
        {DELTA_SECTIONS.map((s) => (
          <Route key={s.slug} path={s.slug} element={PAGES[s.slug]} />
        ))}
        {/* Moved to /policies/:reference under the workspace. Kept as a redirect so
            existing links and bookmarks still land somewhere correct. */}
        <Route path="policies/:reference" element={<RedirectToWorkspace />} />
        <Route path="*" element={<Navigate to="/delta" replace />} />
      </Routes>
    </div>
  );
}

function RedirectToWorkspace() {
  const { reference } = useParams();
  return <Navigate to={`/policies/${encodeURIComponent(reference)}`} replace />;
}

// The section's own front page.
function DeltaIndex() {
  const nav = useNavigate();

  return (
    <div>
      <SlideHead headline="The Plan" />

      {/* Full container width, matching the grid below. Capped at a reading measure it
          broke to three short lines and left the right half of the screen empty above
          full-width cards, which reads as a layout fault rather than as typography. */}
      <p className="mb-6 text-[13px] leading-relaxed text-gray-600 dark:text-gray-300">
        {DELTA_INTRO}
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        {DELTA_SECTIONS.map((s, i) => (
          <button
            key={s.slug}
            type="button"
            onClick={() => nav(s.slug)}
            style={{ ...toneVars(s.tone), animationDelay: `${60 + i * 55}ms` }}
            className="deck-rise group flex flex-col rounded-lg border border-gray-200 bg-white p-4 text-left transition-colors hover:border-brand-gold dark:border-gray-800 dark:bg-[#0d1117]"
          >
            <span className="mb-2.5 flex items-center gap-2">
              <span className="block h-[3px] w-9 rounded-full" style={{ background: TONES[s.tone].bar }} />
              <span className="text-[10px] font-medium tabular-nums text-gray-300 dark:text-gray-600">
                {String(i + 1).padStart(2, '0')}
              </span>
            </span>
            <span className="flex items-start gap-2">
              <span className="min-w-0 flex-1">
                <span className="block font-serif text-[19px] leading-tight text-gray-900 dark:text-gray-50">
                  {s.title}
                </span>
                {/* One tag, and only one. The uppercase kicker that used to sit here
                    repeated the deck's slide sub-heading and said nothing the title did
                    not already say. */}
                <span className="mt-1 block text-[12px] leading-snug text-gray-500 dark:text-gray-400">
                  {s.blurb}
                </span>
              </span>
              <MdChevronRight
                size={20}
                className="mt-0.5 shrink-0 text-gray-300 transition-transform group-hover:translate-x-0.5 group-hover:text-brand-gold dark:text-gray-600"
              />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
